// src/pages/Users.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import { useImpersonation } from '../context/ImpersonationContext';
import DataTable from '../components/DataTable';
import Modal, { ModalForm, ModalConfirm } from '../components/Modal';
import { RoleBadge, StatusBadge } from '../components/Badge';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    EllipsisVerticalIcon,
    UserIcon,
    EnvelopeIcon,
    KeyIcon,
    CheckBadgeIcon,
    ArrowPathIcon,
    ShieldCheckIcon,
    ArrowTopRightOnSquareIcon,
    BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { IdField } from '../components/CopyButton';

// Frontend impersonation URL - opens in new tab for support team
const FRONTEND_IMPERSONATE_URL = 'https://www.bidlyzer.com/admin/impersonate';

const Users = () => {
    const { executeQuery, executeMutation, queries, mutations, loading, error } = useGraphQL();
    const { impersonateUser } = useImpersonation();
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [, setShowSupportModal] = useState(false);
    const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
    const [actionMenuOpen, setActionMenuOpen] = useState(null);
    const [actionResult, setActionResult] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [filters, setFilters] = useState({
        role: '',
        active: '',
        companyId: '',
        representativesOnly: false
    });
    const [companies, setCompanies] = useState([]);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'USER',
        companyId: '',
        companyName: '',
        companySize: 'SMALL',
        jobTitle: '',
        active: true,
        acceptTerms: true
    });
    const [submitLoading, setSubmitLoading] = useState(false);
    const [supportActionLoading, setSupportActionLoading] = useState(false);

    useEffect(() => {
        loadUsers();
        loadCompanies();
    }, [filters]);

    const loadCompanies = async () => {
        try {
            const data = await executeQuery(queries.getCompanies(), { limit: 1000, offset: 0 });
            setCompanies(data.adminCompanies || []);
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const loadUsers = async () => {
        try {
            const filterVars = {
                role: filters.role || undefined,
                active: filters.active !== '' ? filters.active === 'true' : undefined,
                companyId: filters.companyId || undefined,
                limit: 100,
                offset: 0
            };
            const data = await executeQuery(queries.getUsers(), filterVars);
            let fetchedUsers = data.adminUsers || [];

            // Client-side filter for representatives only
            if (filters.representativesOnly) {
                fetchedUsers = fetchedUsers.filter(u => u.isCompanyRepresentative);
            }

            setUsers(fetchedUsers);
        } catch (err) {
            console.error('Failed to load users:', err);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            // Validate required fields for RegisterInput
            if (!formData.companyName || !formData.companySize) {
                alert('Company name and size are required (existing name = user joins that company).');
                setSubmitLoading(false);
                return;
            }

            const isSales = formData.role === 'SALES';
            if (isSales && !formData.password) {
                alert('Sales accounts need an explicit password — you hand it over personally; no mail is sent.');
                setSubmitLoading(false);
                return;
            }

            const input = {
                email: formData.email,
                password: formData.password || `TempPass${Math.random().toString(36).slice(-8)}!`,
                firstName: formData.firstName,
                lastName: formData.lastName,
                companyName: formData.companyName,
                companySize: formData.companySize,
                jobTitle: formData.jobTitle || undefined,
                acceptTerms: true
            };

            // SALES accounts go through the dedicated containment path: role
            // SALES, pre-verified, company WITHOUT a trial subscription, zero
            // mails. Everything else uses the regular createUser + role step.
            const result = isSales
                ? await executeMutation(mutations.createSalesUser, { input })
                : await executeMutation(mutations.createUser, { input });
            const payload = isSales ? result.createSalesUser : result.createUser;

            if (payload.success) {
                setShowCreateModal(false);
                resetForm();
                await loadUsers();
                if (isSales) {
                    setActionResult({ message: `Sales account ready: ${formData.email} — login at sales.bidlyzer.com. ${payload.message || ''}` });
                } else if (formData.role && formData.role !== 'USER' && payload.user) {
                    try {
                        await executeMutation(mutations.updateUser, {
                            id: payload.user.id,
                            input: { role: formData.role }
                        });
                    } catch (err) {
                        console.warn('Failed to update user role:', err);
                    }
                }
            } else {
                alert('Error: ' + payload.error);
            }
        } catch (err) {
            console.error('Failed to create user:', err);
            alert('Failed to create user: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.updateUser, {
                id: selectedUser.id,
                input: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    role: formData.role,
                    jobTitle: formData.jobTitle || undefined,
                    active: formData.active
                }
            });

            if (result.updateUser.success) {
                setShowEditModal(false);
                setSelectedUser(null);
                resetForm();
                await loadUsers();
            } else {
                alert('Error: ' + result.updateUser.error);
            }
        } catch (err) {
            console.error('Failed to update user:', err);
            alert('Failed to update user: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        try {
            const result = await executeMutation(mutations.deleteUser, {
                id: selectedUser.id
            });

            if (result.deleteUser.success) {
                setShowDeleteModal(false);
                setSelectedUser(null);
                await loadUsers();
            } else {
                alert('Error: ' + result.deleteUser.error);
            }
        } catch (err) {
            console.error('Failed to delete user:', err);
            alert('Failed to delete user: ' + err.message);
        }
    };

    // Support Action Handlers
    const showActionResultMessage = (message, isError = false) => {
        setActionResult({ message, isError });
        setTimeout(() => setActionResult(null), 5000);
    };

    const handleImpersonateUser = async (user) => {
        if (user.role === 'ADMIN') {
            showActionResultMessage('Cannot impersonate admin users', true);
            return;
        }

        setSupportActionLoading(true);
        const result = await impersonateUser(user.id);
        setSupportActionLoading(false);

        if (result.success) {
            showActionResultMessage(`Now impersonating ${user.firstName} ${user.lastName}`);
        } else {
            showActionResultMessage(result.error || 'Failed to impersonate user', true);
        }
    };

    const handleImpersonateInFrontend = (user) => {
        if (user.role === 'ADMIN') {
            showActionResultMessage('Cannot impersonate admin users', true);
            return;
        }

        const impersonateUrl = `${FRONTEND_IMPERSONATE_URL}?userId=${encodeURIComponent(user.id)}`;
        window.open(impersonateUrl, '_blank', 'noopener,noreferrer');
        showActionResultMessage(`Opening impersonation session for ${user.firstName} ${user.lastName} in new tab`);
    };

    // Helper to check if error is due to missing backend feature
    const isFeatureNotAvailableError = (err) => {
        return err.message?.includes('Cannot query field') ||
               err.message?.includes('Cannot return null');
    };

    const handleResendVerificationEmail = async (user) => {
        setSupportActionLoading(true);
        try {
            const data = await executeMutation(mutations.adminResendVerificationEmail, { userId: user.id });
            if (data?.adminResendVerificationEmail?.success) {
                showActionResultMessage(`Verification email sent to ${user.email}`);
            } else {
                showActionResultMessage(data?.adminResendVerificationEmail?.error || 'Failed to send verification email', true);
            }
        } catch (err) {
            if (isFeatureNotAvailableError(err)) {
                showActionResultMessage('This feature is not available on the backend yet', true);
            } else {
                showActionResultMessage(err.message, true);
            }
        } finally {
            setSupportActionLoading(false);
        }
    };

    const handleResendPasswordResetEmail = async (user) => {
        setSupportActionLoading(true);
        try {
            const data = await executeMutation(mutations.adminResendPasswordResetEmail, { userId: user.id });
            if (data?.adminResendPasswordResetEmail?.success) {
                showActionResultMessage(`Password reset email sent to ${user.email}`);
            } else {
                showActionResultMessage(data?.adminResendPasswordResetEmail?.error || 'Failed to send password reset email', true);
            }
        } catch (err) {
            if (isFeatureNotAvailableError(err)) {
                showActionResultMessage('This feature is not available on the backend yet', true);
            } else {
                showActionResultMessage(err.message, true);
            }
        } finally {
            setSupportActionLoading(false);
        }
    };

    const handleResendRegistrationEmail = async (user) => {
        setSupportActionLoading(true);
        try {
            const data = await executeMutation(mutations.adminResendRegistrationEmail, { userId: user.id });
            if (data?.adminResendRegistrationEmail?.success) {
                showActionResultMessage(`Registration email sent to ${user.email}`);
            } else {
                showActionResultMessage(data?.adminResendRegistrationEmail?.error || 'Failed to send registration email', true);
            }
        } catch (err) {
            if (isFeatureNotAvailableError(err)) {
                showActionResultMessage('This feature is not available on the backend yet', true);
            } else {
                showActionResultMessage(err.message, true);
            }
        } finally {
            setSupportActionLoading(false);
        }
    };

    const handleSetUserVerified = async (user) => {
        setSupportActionLoading(true);
        try {
            const data = await executeMutation(mutations.adminSetUserVerified, { userId: user.id });
            if (data?.adminSetUserVerified?.success) {
                showActionResultMessage(`${user.email} has been marked as verified`);
                await loadUsers();
            } else {
                showActionResultMessage(data?.adminSetUserVerified?.error || 'Failed to verify user', true);
            }
        } catch (err) {
            if (isFeatureNotAvailableError(err)) {
                showActionResultMessage('This feature is not available on the backend yet', true);
            } else {
                showActionResultMessage(err.message, true);
            }
        } finally {
            setSupportActionLoading(false);
        }
    };

    const handleSetUserPassword = async () => {
        if (!newPassword || newPassword.length < 8) {
            showActionResultMessage('Password must be at least 8 characters', true);
            return;
        }

        setSupportActionLoading(true);
        try {
            const data = await executeMutation(mutations.adminSetUserPassword, {
                userId: selectedUser.id,
                newPassword: newPassword
            });
            if (data?.adminSetUserPassword?.success) {
                showActionResultMessage(`Password updated for ${selectedUser.email}`);
                setShowSetPasswordModal(false);
                setNewPassword('');
                setSelectedUser(null);
            } else {
                showActionResultMessage(data?.adminSetUserPassword?.error || 'Failed to set password', true);
            }
        } catch (err) {
            if (isFeatureNotAvailableError(err)) {
                showActionResultMessage('This feature is not available on the backend yet', true);
            } else {
                showActionResultMessage(err.message, true);
            }
        } finally {
            setSupportActionLoading(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const openSupportModal = (user) => {
        setSelectedUser(user);
        setShowSupportModal(true);
        setActionMenuOpen(null);
    };

    const openSetPasswordModal = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setShowSetPasswordModal(true);
        setShowSupportModal(false);
    };

    const resetForm = () => {
        setFormData({
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            role: 'USER',
            companyId: '',
            companyName: '',
            companySize: 'SMALL',
            jobTitle: '',
            active: true,
            acceptTerms: true
        });
    };

    const openEditModal = (user) => {
        setSelectedUser(user);
        setFormData({
            email: user.email,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            role: user.role,
            companyId: user.companyId || '',
            jobTitle: user.jobTitle || '',
            active: user.active
        });
        setShowEditModal(true);
    };

    const openDeleteModal = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    const columns = [
        {
            key: 'email',
            title: 'User',
            sortable: true,
            render: (email, user) => (
                <div className="flex items-center space-x-3">
                    {user.avatar ? (
                        <img
                            src={user.avatar}
                            alt={`${user.firstName || ''} ${user.lastName || ''}`}
                            className="h-8 w-8 rounded-full object-cover"
                        />
                    ) : (
                        <div className="h-8 w-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm font-medium">
                            {(user.firstName?.[0] || '').toUpperCase()}{(user.lastName?.[0] || '').toUpperCase()}
                        </div>
                    )}
                    <div>
                        <div className="font-medium text-white">{email}</div>
                        <div className="text-sm text-gray-400">
                            {user.firstName} {user.lastName}
                        </div>
                    </div>
                </div>
            )
        },
        {
            key: 'role',
            title: 'Role',
            sortable: true,
            render: (role) => <RoleBadge role={role} />
        },
        {
            key: 'isCompanyRepresentative',
            title: 'Rep',
            render: (isRep) => isRep ? (
                <div className="flex justify-center">
                    <StarIconSolid className="h-5 w-5 text-yellow-700" title="Company Representative" />
                </div>
            ) : null
        },
        {
            key: 'company',
            title: 'Company',
            render: (_, user) => user.company ? (
                <div className="flex items-center">
                    <div>
                        <div className="flex items-center">
                            <span className="text-white">{user.company.name}</span>
                            {user.isCompanyRepresentative && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-50 text-yellow-700">
                                    Rep
                                </span>
                            )}
                        </div>
                        <div className="text-sm text-gray-400">{user.company.industry}</div>
                    </div>
                </div>
            ) : (
                <span className="text-gray-400">No company</span>
            )
        },
        {
            key: 'jobTitle',
            title: 'Job Title',
            render: (title) => title || <span className="text-gray-400">-</span>
        },
        {
            key: 'active',
            title: 'Status',
            render: (active) => <StatusBadge status={active ? 'active' : 'inactive'} />
        },
        {
            key: 'lastLogin',
            title: 'Last Login',
            render: (lastLogin) => lastLogin ?
                new Date(lastLogin).toLocaleDateString() :
                <span className="text-gray-400">Never</span>
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, user) => (
                <div className="flex space-x-2 items-center">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(user);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="Edit user"
                    >
                        <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(user);
                        }}
                        className="p-1 text-gray-400 hover:text-red-700"
                        title="Delete user"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                    {/* Support Actions Menu */}
                    <div className="relative">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setActionMenuOpen(actionMenuOpen === user.id ? null : user.id);
                            }}
                            className="p-1 text-gray-400 hover:text-amber-700"
                            title="Support actions"
                        >
                            <EllipsisVerticalIcon className="h-4 w-4" />
                        </button>
                        {actionMenuOpen === user.id && (
                            <>
                                <div
                                    className="fixed inset-0 z-40"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setActionMenuOpen(null);
                                    }}
                                />
                                <div className="absolute right-0 mt-1 w-56 bg-gray-700 shadow-lg py-1 z-50">
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase border-b border-gray-600">
                                        Support Actions
                                    </div>
                                    {user.company && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActionMenuOpen(null);
                                                // Navigate to Companies page filtered to this company
                                                window.location.href = `/companies?id=${user.company.id}`;
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm text-blue-700 hover:bg-gray-600 flex items-center space-x-2"
                                        >
                                            <BuildingOfficeIcon className="h-4 w-4" />
                                            <span>View Company</span>
                                        </button>
                                    )}
                                    {user.role !== 'ADMIN' && (
                                        <>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActionMenuOpen(null);
                                                    handleImpersonateInFrontend(user);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-gray-600 flex items-center space-x-2"
                                            >
                                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                                <span>Impersonate in Frontend</span>
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActionMenuOpen(null);
                                                    handleImpersonateUser(user);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-600 flex items-center space-x-2"
                                            >
                                                <UserIcon className="h-4 w-4" />
                                                <span>Impersonate (Backstage)</span>
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActionMenuOpen(null);
                                            handleResendVerificationEmail(user);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-600 flex items-center space-x-2"
                                    >
                                        <EnvelopeIcon className="h-4 w-4" />
                                        <span>Resend Verification Email</span>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActionMenuOpen(null);
                                            handleResendPasswordResetEmail(user);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-600 flex items-center space-x-2"
                                    >
                                        <KeyIcon className="h-4 w-4" />
                                        <span>Send Password Reset</span>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActionMenuOpen(null);
                                            handleResendRegistrationEmail(user);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-600 flex items-center space-x-2"
                                    >
                                        <ArrowPathIcon className="h-4 w-4" />
                                        <span>Resend Welcome Email</span>
                                    </button>
                                    <div className="border-t border-gray-600 my-1" />
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActionMenuOpen(null);
                                            handleSetUserVerified(user);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-gray-600 flex items-center space-x-2"
                                    >
                                        <CheckBadgeIcon className="h-4 w-4" />
                                        <span>Mark Email Verified</span>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActionMenuOpen(null);
                                            openSetPasswordModal(user);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-blue-700 hover:bg-gray-600 flex items-center space-x-2"
                                    >
                                        <ShieldCheckIcon className="h-4 w-4" />
                                        <span>Set New Password</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-white">Users</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowCreateModal(true);
                    }}
                    className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 flex items-center space-x-2"
                >
                    <PlusIcon className="h-4 w-4" />
                    <span>Add User</span>
                </button>
            </div>

            {/* Action Result Notification */}
            {actionResult && (
                <div className={`${actionResult.isError ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border p-4`}>
                    <p className={actionResult.isError ? 'text-red-700' : 'text-green-700'}>
                        {actionResult.message}
                    </p>
                </div>
            )}

            {/* Loading indicator for support actions */}
            {supportActionLoading && (
                <div className="bg-amber-50 border border-amber-200 p-4 flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-400"></div>
                    <p className="text-amber-700">Processing support action...</p>
                </div>
            )}

            {/* Filters */}
            <div className="bg-gray-800 p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                        <select
                            value={filters.role}
                            onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All Roles</option>
                            <option value="ADMIN">Admin</option>
                            <option value="MANAGER">Manager</option>
                            <option value="USER">User</option>
                            <option value="SALES">Sales (external agency)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                        <select
                            value={filters.active}
                            onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        >
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Company ID</label>
                        <input
                            type="text"
                            value={filters.companyId}
                            onChange={(e) => setFilters(prev => ({ ...prev, companyId: e.target.value }))}
                            placeholder="Filter by company ID"
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={filters.representativesOnly}
                            onChange={(e) => setFilters(prev => ({ ...prev, representativesOnly: e.target.checked }))}
                            className="w-4 h-4 bg-gray-700 border-gray-600 text-primary focus:ring-primary focus:ring-offset-gray-800"
                        />
                        <span className="text-sm text-gray-300 flex items-center">
                            <StarIconSolid className="h-4 w-4 text-yellow-700 mr-1" />
                            Representatives Only
                        </span>
                    </label>
                    <button
                            onClick={() => setFilters({ role: '', active: '', companyId: '', representativesOnly: false })}
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                        >
                            Clear Filters
                        </button>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 p-4">
                    <p className="text-red-700">Error: {error}</p>
                </div>
            )}

            {/* Users Table */}
            <DataTable
                columns={columns}
                data={users}
                loading={loading}
                onRowClick={(user) => openEditModal(user)}
            />

            {/* Create User Modal */}
            <ModalForm
                isOpen={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    resetForm();
                }}
                onSubmit={handleCreateUser}
                title="Create User"
                submitLabel="Create User"
                isLoading={submitLoading}
            >
                <UserForm formData={formData} setFormData={setFormData} companies={companies} />
            </ModalForm>

            {/* Edit User Modal */}
            <ModalForm
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    resetForm();
                }}
                onSubmit={handleUpdateUser}
                title="Edit User"
                submitLabel="Update User"
                isLoading={submitLoading}
            >
                <UserForm formData={formData} setFormData={setFormData} isEdit userId={selectedUser?.id} companyId={selectedUser?.companyId} />
            </ModalForm>

            {/* Delete Confirmation Modal */}
            <ModalConfirm
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                }}
                onConfirm={handleDeleteUser}
                title="Delete User"
                message={`Are you sure you want to delete user "${selectedUser?.email}"? This action cannot be undone.`}
                confirmLabel="Delete"
                type="danger"
            />

            {/* Set Password Modal */}
            <Modal
                isOpen={showSetPasswordModal}
                onClose={() => {
                    setShowSetPasswordModal(false);
                    setSelectedUser(null);
                    setNewPassword('');
                }}
                title="Set New Password"
            >
                <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 p-3">
                        <p className="text-amber-700 text-sm">
                            This will set a new password for <strong>{selectedUser?.email}</strong>.
                            The user will not be notified of this change.
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            New Password
                        </label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="Enter new password (min 8 characters)"
                            minLength={8}
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={() => {
                                setShowSetPasswordModal(false);
                                setSelectedUser(null);
                                setNewPassword('');
                            }}
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSetUserPassword}
                            disabled={supportActionLoading || !newPassword || newPassword.length < 8}
                            className="px-4 py-2 bg-primary text-white hover:bg-primary/80 disabled:opacity-50"
                        >
                            {supportActionLoading ? 'Setting...' : 'Set Password'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

// User Form Component
const UserForm = ({ formData, setFormData, isEdit = false, companies = [], userId, companyId }) => {
    return (
        <div className="space-y-4">
            {/* ID Fields - Only show when editing */}
            {isEdit && userId && (
                <div className="space-y-2">
                    <IdField label="User ID" value={userId} />
                    {companyId && <IdField label="Company ID" value={companyId} />}
                </div>
            )}

            {!isEdit && (
                <>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Password <span className="text-gray-400 text-xs">(leave empty to auto-generate)</span>
                        </label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="Auto-generated if left empty"
                        />
                    </div>
                </>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">First Name *</label>
                    <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Last Name *</label>
                    <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                    />
                </div>
            </div>

            {!isEdit && (
                <div className="border-t border-gray-600 pt-4">
                    <h3 className="text-sm font-medium text-white mb-3">Company</h3>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Company Name *</label>
                            <input
                                type="text"
                                list="company-suggestions"
                                value={formData.companyName}
                                onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value, companyId: '' }))}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                placeholder="Type to search or create"
                                required
                            />
                            <datalist id="company-suggestions">
                                {companies.map(company => (
                                    <option key={company.id} value={company.name} />
                                ))}
                            </datalist>
                            <p className="text-xs text-gray-400 mt-1">
                                Existing name → the user joins that company. New name → the company is created.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Company Size *</label>
                            <select
                                value={formData.companySize}
                                onChange={(e) => setFormData(prev => ({ ...prev, companySize: e.target.value }))}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                required
                            >
                                <option value="FREELANCER">Freelancer</option>
                                <option value="MICRO">Micro (1-9)</option>
                                <option value="SMALL">Small (10-49)</option>
                                <option value="MEDIUM">Medium (50-249)</option>
                                <option value="LARGE">Large (250+)</option>
                            </select>
                            <p className="text-xs text-gray-400 mt-1">Used only when a new company is created.</p>
                        </div>
                    </div>
                    {formData.role === 'SALES' && (
                        <p className="text-xs text-emerald-400 mt-3">
                            Sales account: pre-verified, no trial subscription, no mails — set the password here and hand it over personally. Login: sales.bidlyzer.com
                        </p>
                    )}
                </div>
            )}

            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Job Title</label>
                <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, jobTitle: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                    Role {!isEdit && <span className="text-gray-400 text-xs">(applied after user creation)</span>}
                </label>
                <select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                >
                    <option value="USER">User</option>
                    <option value="MANAGER">Manager</option>
                    <option value="ADMIN">Admin</option>
                    <option value="SALES">Sales (external agency)</option>
                </select>
            </div>

            {isEdit && (
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="active"
                        checked={formData.active}
                        onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-600"
                    />
                    <label htmlFor="active" className="ml-2 block text-sm text-gray-300">
                        Active
                    </label>
                </div>
            )}
        </div>
    );
};

export default Users;